import { forwardRef, ReactNode } from 'react';
import MuiMenuItem, { menuItemClasses, MenuItemProps as MuiMenuItemProps } from '@mui/material/MenuItem';
import Stack from '../Stack';
import { alpha, styled } from '@mui/material/styles';
import colors from '../theme/colors';

export interface MenuItemProps extends MuiMenuItemProps {
  startIcon?: ReactNode;
  endIcon?: ReactNode;
  internal?: boolean;
}

const StyledMenuItem = styled(MuiMenuItem, {
  name: 'O2MenuItem',
  slot: 'Root',
  shouldForwardProp: (prop) => prop !== 'internal',
})<MenuItemProps>(({ theme, internal }) => ({
  '&:first-of-type': {
    borderTopLeftRadius: theme.shape.borderRadius,
    borderTopRightRadius: theme.shape.borderRadius,
  },
  '&:hover': {
    backgroundColor: colors.brandPrimary[100],
  },
  '&:last-of-type': {
    borderBottomLeftRadius: `calc(${theme.shape.borderRadius}px - 2px)`,
    borderBottomRightRadius: `calc(${theme.shape.borderRadius}px - 2px)`,
  },
  [`&.${menuItemClasses.focusVisible}`]: {
    border: `2px solid ${colors.brandPrimary[400]}`,
    borderLeftWidth: '1px',
    borderRightWidth: '1px',
    padding: '4px 15px',
    backgroundColor: colors.basic.white,
    '&:hover': {
      backgroundColor: colors.brandPrimary[100],
    },
  },
  [`&.${menuItemClasses.selected}`]: {
    backgroundColor: colors.solidGray[100],
    '&:hover': {
      backgroundColor: colors.brandPrimary[100],
    },
    [`&.${menuItemClasses.focusVisible}`]: {
      backgroundColor: colors.solidGray[100],
      '&:hover': {
        backgroundColor: colors.brandPrimary[100],
      },
    },
  },
  ...(internal && {
    backgroundColor: theme.palette.internal.light,
    '&:hover': {
      backgroundColor: colors.brandPrimary[100],
    },
    [`&.${menuItemClasses.selected}`]: {
      backgroundColor: colors.internal[200],
      '&:hover': {
        backgroundColor: colors.brandPrimary[100],
      },
      [`&.${menuItemClasses.focusVisible}`]: {
        backgroundColor: colors.internal[200],
        '&:hover': {
          backgroundColor: colors.brandPrimary[100],
        },
      },
    },
    [`&.${menuItemClasses.focusVisible}`]: {
      border: `2px solid ${colors.brandPrimary[400]}`,
      borderRightWidth: '1px',
      borderLeftWidth: '1px',
      padding: '4px 15px',
      backgroundColor: theme.palette.internal.light,
      '&:hover': {
        backgroundColor: colors.brandPrimary[100],
      },
    },
  }),
}));

const MenuItem = forwardRef<HTMLLIElement, MenuItemProps>((props, ref) => {
  const { startIcon, endIcon, internal, children, ...otherProps } = props;

  return (
    <StyledMenuItem ref={ref} internal={internal} {...otherProps}>
      <Stack direction="row" alignItems="center" gap={1} width="100%">
        {startIcon && (
          <Stack direction="row" alignItems="center" gap={0.5}>
            {startIcon}
          </Stack>
        )}
        <>{children}</>
        {endIcon && (
          <Stack direction="row" alignItems="center" gap={0.5} sx={{ ml: 'auto' }}>
            {endIcon}
          </Stack>
        )}
      </Stack>
    </StyledMenuItem>
  );
});

MenuItem.displayName = 'MenuItem';

export default MenuItem;
